
import { Component, Watch } from 'vue-property-decorator';
import { RouteNames } from '@/enums/routes/RouteNames';
import SettingsTopBar from '@/components/views/settings/SettingsTopBar.vue';
import { mixins } from 'vue-class-component';
import { UserRightsMixin } from '@/mixins/UserRights';
import NotAllowedComponent from '@/components/views/settings/NotAllowedComponent.vue';
import ProductVersionsTable from '@/components/views/CmsIndex/ProductVersionsTable.vue';
import { ProductVersionTabs } from '@/enums/global/ProductVersionTabs';
import { tableConfig } from '@/helpers/CmsIndexHelper';
import { debounce } from 'vue-debounce';
import { downloadExcelFile } from '@/helpers/ProductVersionHelper';
import ProductImages from '@/components/views/CmsIndex/ProductImages.vue';
import ProductVersionPricesTable from '@/components/views/CmsIndex/ProductVersionPricesTable.vue';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import ProductMaterialSelector from '@/components/views/CmsIndex/ProductMaterialSelector.vue';

@Component({
    name: 'ProductVersions',
    components: {
        SettingsTopBar,
        NotAllowedComponent,
        ProductVersionsTable,
        ProductImages,
        ProductVersionPricesTable,
        ProductMaterialSelector,
    },
})
export default class ProductVersions extends mixins<UserRightsMixin>(UserRightsMixin) {
    private backRoute: RouteNames = RouteNames.settings;
    private tableConfig = tableConfig;
    private activeKey = ProductVersionTabs.PRODUCT_VERSIONS;
    private searchText = '';
    private filteredData: any = [];
    private debouncedSearch = debounce(() => {
        this.applyFiltersAndSearch();
    }, 1000);
    private selectedEntity: any = null;
    private isImagesModalVisible = false;
    private isPricesModalVisible = false;
    private loadingOverlay = new LoadingOverlayHelper(this, {});
    private prices: any[] = [];
    private isProductMaterialSelectorVisible = false;
    private commodityGroups = ['Ceniki'];
    private productGroups = ['Ceniki', 'Ceniki-Doplačila'];
    private materialFilters = {
        productGroup: '',
        commodityGroup: '',
    };

    private get userHasRights() {
        return this.isSuperAdmin;
    }

    private get currentProductTableConfig() {
        return this.tableConfig.find((config: any) => config.entityName === this.activeKey);
    }

    private get searchPlaceholder() {
        switch (this.activeKey) {
            case ProductVersionTabs.PRODUCT_VERSIONS:
                return this.$t('Search product versions by any field');
            case ProductVersionTabs.PRODUCT_MATERIALS:
                return this.$t('Search product materials by any field');
            default:
                return this.$t('Search product functions by any field');
        }
    }

    private get productTableConfig() {
        return this.tableConfig.filter((config: any) => config.entityName !== 'Transferred orders');
    }

    private get isMaterialsTab() {
        return this.activeKey === ProductVersionTabs.PRODUCT_MATERIALS;
    }

    private get tableData(): any[] {
        return this.tableConfig?.find((config: any) => config.entityName === this.activeKey)?.tableData() || [];
    }

    private onTabChange() {
        this.searchText = '';
        this.setInitialTableData();
        this.materialFilters = {
            productGroup: '',
            commodityGroup: '',
        };
    }

    private async fetchPrices(row: any) {
        this.loadingOverlay.start();

        try {
            const config = this.tableConfig.find((config: any) => config.entityName === this.activeKey);
            const pricesData = config?.additionalEntityFetchEndpoint
                ? await config.additionalEntityFetchEndpoint(row.key)
                : null;

            if (pricesData) {
                this.prices = pricesData.data;
            }
        } catch {
            this.$notification.error({
                message: this.$t('Error') as string,
                description: this.$t('Failed to fetch prices data') as string,
            });
        } finally {
            this.loadingOverlay.stop();
        }
    }

    private async onRowClick(row: any) {
        switch (this.activeKey) {
            case ProductVersionTabs.PRODUCT_VERSIONS:
                if (!this.isSuperAdmin) {
                    this.$notification.error({
                        message: this.$t('Error') as string,
                        description: this.$t('You do not have permission to view prices') as string,
                    });
                    return;
                }
                await this.fetchPrices(row);
                this.openPricesModal();
                break;
            case ProductVersionTabs.PRODUCT_MATERIALS:
                this.openProductMaterialSelector();
                break;
            default:
                return;
        }

        this.selectedEntity = row;
    }

    private openProductMaterialSelector() {
        this.isProductMaterialSelectorVisible = true;
    }

    private openImagesModal() {
        this.isImagesModalVisible = true;
    }

    private openPricesModal() {
        this.isPricesModalVisible = true;
    }

    private openMaterialPricesModal(row: any) {
        if (!this.isSuperAdmin) {
            this.$notification.error({
                message: this.$t('Error') as string,
                description: this.$t('You do not have permission to view prices') as string,
            });
            return;
        }

        this.prices = [
            {
                price1: this.selectedEntity?.priceOne,
                price2: this.selectedEntity?.priceTwo,
                name: this.selectedEntity.name,
            },
        ] as any;

        this.openPricesModal();
    }

    private closeImagesModal() {
        this.isImagesModalVisible = false;
    }

    private closePricesModal() {
        this.isPricesModalVisible = false;
    }

    private closeProductMaterialSelectorModal() {
        this.isProductMaterialSelectorVisible = false;
    }

    private async setInitialTableData() {
        if (!this.tableData) {
            return;
        }

        await this.$nextTick();
        this.filteredData = this.tableData;
    }

    private onFilterChange() {
        // Apply combined filtering logic on tab change or filter change
        this.applyFiltersAndSearch();
    }

    @Watch('searchText')
    private onSearchTextChange(val: string) {
        if (!val && !this.materialFilters.commodityGroup && !this.materialFilters.productGroup) {
            // Reset to original data when no filters are applied
            this.filteredData = this.tableData;
            return;
        }

        // Use debounced search to improve performance
        this.debouncedSearch();
    }

    /**
     * Combined filtering and searching for efficient data processing
     * Applies all filters in a single pass through the data
     */
    private applyFiltersAndSearch() {
        if (!this.tableData || this.tableData.length === 0) {
            return;
        }

        // Use cached original data to avoid multiple backend calls
        const hasSearchText = this.searchText.trim().length > 0;
        const hasFilters = this.materialFilters.commodityGroup || this.materialFilters.productGroup;

        // Filter data in a single pass through the dataset
        const filteredTableData = this.tableData.filter((row: any) => {
            // First check material filters (faster check)
            const passesFilters =
                !hasFilters ||
                Object.entries(this.materialFilters).every(([filterKey, filterValue]: [string, any]) => {
                    if (!filterValue) return true;
                    return row[filterKey] === filterValue;
                });

            // If row doesn't pass material filters, skip the search text check
            if (!passesFilters) return false;

            // If no search text, row passes if it passes filters
            if (!hasSearchText) return true;

            // Now check search text based on current tab
            switch (this.activeKey) {
                case ProductVersionTabs.PRODUCT_VERSIONS:
                    return this.matchSearchTextForProduct(row);
                default:
                    return this.matchSearchText(row);
            }
        });

        if (filteredTableData.length === 0) {
            this.$notification.info({
                message: this.$t('No results found') as string,
                description: this.$t('Try searching for something else') as string,
            });
        }

        // Update filteredData only at the end of the process
        this.filteredData = filteredTableData;
    }

    /**
     * Check if a row matches the search text for product versions
     * @param row The row to check
     * @returns True if the row matches the search text
     */
    private matchSearchTextForProduct(row: any): boolean {
        const searchText = this.searchText.toLowerCase();

        // First check direct properties (faster)
        for (const [key, value] of Object.entries(row)) {
            if (key === 'key') continue;

            if (
                (typeof value === 'string' || typeof value === 'number') &&
                String(value).toLowerCase().includes(searchText)
            ) {
                return true;
            }

            // Check nested properties if value is an object
            if (value && typeof value === 'object') {
                for (const nestedValue of Object.values(value)) {
                    if (
                        (typeof nestedValue === 'string' || typeof nestedValue === 'number') &&
                        String(nestedValue).toLowerCase().includes(searchText)
                    ) {
                        return true;
                    }
                }
            }
        }

        return false;
    }

    /**
     * Check if a row matches the search text for regular items
     * @param row The row to check
     * @returns True if the row matches the search text
     */
    private matchSearchText(row: any): boolean {
        const searchText = this.searchText.toLowerCase();

        // Check only direct properties
        for (const [key, value] of Object.entries(row)) {
            if (key === 'key') continue;

            if (
                (key === 'priceOne' || key === 'priceTwo') &&
                Array.isArray(value) &&
                String(value?.[0]?.[0]).includes(searchText)
            ) {
                return true;
            }

            if (
                (typeof value === 'string' || typeof value === 'number') &&
                String(value).toLowerCase().includes(searchText)
            ) {
                return true;
            }
        }

        return false;
    }

    private async downloadExcel() {
        const data = this.tableConfig.find((config: any) => config.entityName === this.activeKey)?.tableData();

        try {
            await this.notifyUserForPrinting(data);
        } catch (e) {
            return;
        }

        this.downloadFile(data);
    }

    private downloadFile(data: any) {
        try {
            downloadExcelFile(data as any[], this.activeKey, ['key']);
        } catch (e) {
            this.$notification.error({
                message: this.$t('Error') as string,
                description: this.$t('An error occurred while downloading the Excel file') as string,
            });
        }
    }

    private async downloadExcelForPrices({ data, filename }: any) {
        try {
            await this.notifyUserForPrinting(data);
        } catch (e) {
            return;
        }

        try {
            // Create a modified copy of the data specifically for the Excel output
            const modifiedData = data.map((row: any[], index: number) => {
                // Only modify the first row by adding an empty cell at the beginning
                if (index === 0) {
                    return ['', ...row]; // Add empty string as first cell
                }
                return [...row]; // Return shallow copy of other rows
            });

            downloadExcelFile(modifiedData as any[], filename, [], false);
        } catch (e) {
            this.$notification.error({
                message: this.$t('Error') as string,
                description: this.$t('An error occurred while downloading the Excel file') as string,
            });
        }
    }

    private async notifyUserForPrinting(data: any) {
        if (!data || !data.length) {
            this.$notification.error({
                message: this.$t('Error') as string,
                description: this.$t('Could not find data, please try again') as string,
            });

            return Promise.reject();
        }

        this.$notification.info({
            message: this.$t('Downloading Excel file') as string,
            description: this.$t('Please wait while the file is being prepared') as string,
            duration: 3,
        });
    }
}
